import DateTimeControl from '@/components/form-control/DateTimeControl'
import ErrorMessage from '@/components/form-control/ErrorMessage'
import InputFieldCalendar from '@/components/form-control/InputFieldCalendar'
import InputLabelField from '@/components/form-control/InputLabelField'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import useAddReference from '@/features/config/hooks/etReference/useAddReference'
import useEtReferenceById from '@/features/config/hooks/etReference/useEtReferenceById'
import useUpdateReference from '@/features/config/hooks/etReference/useUpdateReference'
import useTranslation from '@/hooks/useTranslation'
import {
  etValidToFromServer,
  etValidToPayload,
} from '@/features/config/pages/EtReference/etValidTo'
import { useUnits } from '@/hooks/useUnits'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

interface HistoricalViewProps {
  id?: string
}

const schema = yup
  .object({
    description: yup.string().required('Description is required'),
    remoteETperDay: yup
      .number()
      .typeError('Remote ET PerDay is a number')
      .required('Remote ET PerDay is required'),
    remoteETValidTo: yup
      .string()
      .typeError('RemoteETValidTo is required')
      .required('RemoteETValidTo is required'),
    january: yup
      .number()
      .typeError('January is required')
      .min(0, 'January must be at least 0')
      .max(65, 'January Maximum is 65'),
    february: yup
      .number()
      .typeError('February is required')
      .min(0, 'February must be at least 0')
      .max(65, 'February Maximum is 65'),
    march: yup
      .number()
      .typeError('March is required')
      .min(0, 'March must be at least 0')
      .max(65, 'March Maximum is 65'),
    april: yup
      .number()
      .typeError('April is required')
      .min(0, 'April must be at least 0')
      .max(65, 'April Maximum is 65'),

    may: yup
      .number()
      .typeError('May is required')
      .min(0, 'May must be at least 0')
      .max(65, 'May Maximum is 65'),
    june: yup
      .number()
      .typeError('June is required')
      .min(0, 'June must be at least 0')
      .max(65, 'June Maximum is 65'),
    july: yup
      .number()
      .typeError('July is required')
      .min(0, 'July must be at least 0')
      .max(65, 'July Maximum is 65'),
    august: yup
      .number()
      .typeError('August is required')
      .min(0, 'August must be at least 0')
      .max(65, 'August Maximum is 65'),
    september: yup
      .number()
      .typeError('September is required')
      .min(0, 'September must be at least 0')
      .max(65, 'September Maximum is 65'),
    october: yup
      .number()
      .typeError('October is required')
      .min(0, 'October must be at least 0')
      .max(65, 'October Maximum is 65'),
    november: yup
      .number()
      .typeError('November is required')
      .min(0, 'November must be at least 0')
      .max(65, 'November Maximum is 65'),
    december: yup
      .number()
      .typeError('December is required')
      .min(0, 'December must be at least 0')
      .max(65, 'December Maximum is 65'),
  })
  .required()

const EtReferenceView = ({ id }: HistoricalViewProps) => {
  const { closeSidebar } = useConfigUI()
  const { t } = useTranslation()
  const { interfaceId } = useAuthContext()
  const { convertFromMetric, convertLabelUnit, convertToMetric } = useUnits()

  const { mutate: addReference, isLoading: loadingAddReference } =
    useAddReference()

  const { mutate: editReference, isLoading: loadingEditReference } =
    useUpdateReference()

  const inputRefSiteName = useRef<HTMLInputElement>(null)

  const { data: editEtReference, isLoading: isLoadingData } =
    useEtReferenceById(interfaceId, id)

  useEffect(() => {
    if (inputRefSiteName.current) {
      inputRefSiteName.current.focus()
    }
  }, [])

  const mode = id ? 'edit' : 'create'
  const isLoading = loadingAddReference || loadingEditReference

  const form = useForm({
    defaultValues: {
      id: '',
      description: '',
      remoteETperDay: '',
      remoteETValidTo: new Date(),
      january: '',
      february: '',
      march: '',
      april: '',
      may: '',
      june: '',
      july: '',
      august: '',
      september: '',
      october: '',
      november: '',
      december: '',
      ...editEtReference,
    },
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    if (editEtReference?.id) {
      form.reset({
        id: editEtReference.id,
        description: editEtReference.description,
        remoteETperDay: convertFromMetric(editEtReference.remoteETperDay, 'mm'),
        // Read as UTC, which is how it is stored (SCRUM-1032 QC, 30 Oct).
        remoteETValidTo: etValidToFromServer(editEtReference.remoteETValidTo),
        january: convertFromMetric(editEtReference.january, 'mm'),
        february: convertFromMetric(editEtReference.february, 'mm'),
        march: convertFromMetric(editEtReference.march, 'mm'),
        april: convertFromMetric(editEtReference.april, 'mm'),
        may: convertFromMetric(editEtReference.may, 'mm'),
        june: convertFromMetric(editEtReference.june, 'mm'),
        july: convertFromMetric(editEtReference.july, 'mm'),
        august: convertFromMetric(editEtReference.august, 'mm'),
        september: convertFromMetric(editEtReference.september, 'mm'),
        october: convertFromMetric(editEtReference.october, 'mm'),
        november: convertFromMetric(editEtReference.november, 'mm'),
        december: convertFromMetric(editEtReference.december, 'mm'),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editEtReference])

  const [disableSave, setDisableSave] = useState(false)

  const handleSave = (data: any) => {
    // Sent in UTC: formatted in local time it moved by the browser's offset
    // on every save (SCRUM-1032 QC, 30 Oct).
    const formattedDate = data.remoteETValidTo
      ? etValidToPayload(new Date(data.remoteETValidTo))
      : '01-01-2024 07:00:00'

    // Convert all mm values to metric before API submission
    const remoteETperDayMetric = convertToMetric(data.remoteETperDay, 'mm')
    const januaryMetric = convertToMetric(data.january || 0, 'mm')
    const februaryMetric = convertToMetric(data.february || 0, 'mm')
    const marchMetric = convertToMetric(data.march || 0, 'mm')
    const aprilMetric = convertToMetric(data.april || 0, 'mm')
    const mayMetric = convertToMetric(data.may || 0, 'mm')
    const juneMetric = convertToMetric(data.june || 0, 'mm')
    const julyMetric = convertToMetric(data.july || 0, 'mm')
    const augustMetric = convertToMetric(data.august || 0, 'mm')
    const septemberMetric = convertToMetric(data.september || 0, 'mm')
    const octoberMetric = convertToMetric(data.october || 0, 'mm')
    const novemberMetric = convertToMetric(data.november || 0, 'mm')
    const decemberMetric = convertToMetric(data.december || 0, 'mm')

    if (mode === 'create') {
      addReference({
        interfaceId: Number(interfaceId||0),
        description: data.description,
        remoteETperDay: remoteETperDayMetric,
        remoteETValidTo: formattedDate || '05-04-2000 17:03:00',
        january: januaryMetric,
        february: februaryMetric,
        march: marchMetric,
        april: aprilMetric,
        may: mayMetric,
        june: juneMetric,
        july: julyMetric,
        august: augustMetric,
        september: septemberMetric,
        october: octoberMetric,
        november: novemberMetric,
        december: decemberMetric,
      })
    }
    if (mode === 'edit') {
      editReference({
        id: Number(id),
        interfaceId: Number(interfaceId||0),
        description: data.description,
        remoteETperDay: remoteETperDayMetric,
        remoteETValidTo: formattedDate || '',
        january: Number(januaryMetric),
        february: Number(februaryMetric),
        march: Number(marchMetric),
        april: Number(aprilMetric),
        may: Number(mayMetric),
        june: Number(juneMetric),
        july: Number(julyMetric),
        august: Number(augustMetric),
        september: Number(septemberMetric),
        october: Number(octoberMetric),
        november: Number(novemberMetric),
        december: Number(decemberMetric),
      })
    }
  }

  return (
    <Container sx={{ width: '450px', height: '100vh' }}>
      {isLoadingData && (
        <Stack height="100%" justifyContent="center" alignItems="center">
          <CircularProgress
            sx={{
              color: 'text.main',
            }}
          />
        </Stack>
      )}
      <Stack>
        <form onSubmit={form.handleSubmit(handleSave)}>
          <Stack sx={{ padding: '34px 18px' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '377px' }}
            >
              <Typography
                sx={{
                  fontWeight: '500',
                  lineHeight: '14px',
                  color: 'text.main',
                }}
              >
                {id ? t('dataConfigEditETReference') : t('dataConfigAddETReference')}
              </Typography>
              <Stack direction="row">
                <Button
                  variant="secondary"
                  sx={{ marginRight: '7px', width: '85px', height: '26px' }}
                  onClick={() => closeSidebar()}
                >
                  {t('commonActionsCancel')}
                </Button>
                <LoadingButton
                  loading={isLoading}
                  size="small"
                  variant="primary"
                  sx={{ minWidth: '72px', height: '26px' }}
                  type="submit"
                  disabled={disableSave}
                >
                  {t('commonActionsSave')}
                </LoadingButton>
              </Stack>
            </Stack>

            <InputLabelField
              name="description"
              form={form}
              label={t('commonLabelsDescription')}
              placeholder={t('commonLabelsDescription')}
              inputRef={inputRefSiteName}
              style={{
                height: 50,
                borderRadius: '4px',
                width: { xs: '377px' },
                marginTop: '8px',
              }}
              padding="8px 16px"
            />

            <InputLabelField
              name="remoteETperDay"
              form={form}
              label={`${t('ETReferenceTableHeaderRemoteETPerDay')} (${convertLabelUnit('mm')})`}
              placeholder="0.00"
              style={{
                height: 50,
                borderRadius: '4px',
                width: { xs: '377px' },
                marginTop: '8px',
              }}
              padding="8px 16px"
            />

            <Stack
              width="377px"
            >
              <DateTimeControl
                form={form}
                name="remoteETValidTo"
                label={t('ETReferenceTableHeaderRemoteETValidTo')}
                placeholder="DD-MM-YYYY HH:mm:ss"
                minDate={new Date('01-01-2020 07:00:00')}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  width: { xs: '377px' },
                  marginTop: '8px',
                  padding: '2px 16px',
                }}
              />
            </Stack>

            <Stack width="377px">
              <Stack direction="row">
                <InputFieldCalendar
                  name="january"
                  form={form}
                  label={t('shortMonthJan')}
                  placeholder="Data"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    width: { xs: '69px' },
                    marginTop: '8px',
                  }}
                  padding="6px 16px"
                />
                <Box sx={{ marginLeft: '8px' }}>
                  <InputFieldCalendar
                    name="february"
                    form={form}
                    label={t('shortMonthFeb')}
                    placeholder="Data"
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '69px' },
                      marginTop: '8px',
                    }}
                    padding="6px 16px"
                  />
                </Box>
                <Box sx={{ marginLeft: '8px' }}>
                  <InputFieldCalendar
                    name="march"
                    form={form}
                    label={t('shortMonthMar')}
                    placeholder="Data"
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '69px' },
                      marginTop: '8px',
                    }}
                    padding="6px 16px"
                  />
                </Box>
                <Box sx={{ marginLeft: '8px' }}>
                  <InputFieldCalendar
                    name="april"
                    form={form}
                    label={t('shortMonthApr')}
                    placeholder="Data"
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '69px' },
                      marginTop: '8px',
                    }}
                    padding="6px 16px"
                  />
                </Box>
                <Box sx={{ marginLeft: '8px' }}>
                  <InputFieldCalendar
                    name="may"
                    form={form}
                    label={t('shortMonthMay')}
                    placeholder="Data"
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '69px' },
                      marginTop: '8px',
                    }}
                    padding="6px 16px"
                  />
                </Box>
              </Stack>

              <Stack direction="row">
                <InputFieldCalendar
                  name="june"
                  form={form}
                  label={t('shortMonthJun')}
                  placeholder="Data"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    width: { xs: '69px' },
                    marginTop: '8px',
                  }}
                  padding="6px 16px"
                />
                <Box sx={{ marginLeft: '8px' }}>
                  <InputFieldCalendar
                    name="july"
                    form={form}
                    label={t('shortMonthJul')}
                    placeholder="Data"
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '69px' },
                      marginTop: '8px',
                    }}
                    padding="6px 16px"
                  />
                </Box>
                <Box sx={{ marginLeft: '8px' }}>
                  <InputFieldCalendar
                    name="august"
                    form={form}
                    label={t('shortMonthAug')}
                    placeholder="Data"
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '69px' },
                      marginTop: '8px',
                    }}
                    padding="6px 16px"
                  />
                </Box>
                <Box sx={{ marginLeft: '8px' }}>
                  <InputFieldCalendar
                    name="september"
                    form={form}
                    label={t('shortMonthSep')}
                    placeholder="Data"
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '69px' },
                      marginTop: '8px',
                    }}
                    padding="6px 16px"
                  />
                </Box>
                <Box sx={{ marginLeft: '8px' }}>
                  <InputFieldCalendar
                    name="october"
                    form={form}
                    label={t('shortMonthOct')}
                    placeholder="Data"
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '69px' },
                      marginTop: '8px',
                    }}
                    padding="6px 16px"
                  />
                </Box>
              </Stack>

              <Stack direction="row">
                <InputFieldCalendar
                  name="november"
                  form={form}
                  label={t('shortMonthNov')}
                  placeholder="Data"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    width: { xs: '69px' },
                    marginTop: '8px',
                  }}
                  padding="6px 16px"
                />
                <Box sx={{ marginLeft: '8px' }}>
                  <InputFieldCalendar
                    name="december"
                    form={form}
                    label={t('shortMonthDec')}
                    placeholder="Data"
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '69px' },
                      marginTop: '8px',
                    }}
                    padding="6px 16px"
                  />
                </Box>
              </Stack>
            </Stack>
            <ErrorMessage
              sx={{ mt: '5px' }}
              message={
                Object.keys(form.formState.errors)
                  .map((month) => form.formState.errors[month]?.message)
                  .find((message) => typeof message === 'string') || ''
              }
            />
          </Stack>
        </form>
      </Stack>
    </Container>
  )
}

export default EtReferenceView
